import { ChangeEvent, Dispatch } from 'react';
import {
  Row, Col,
  Radios,
} from 'nhsuk-react-components';
import { conditionTargetToLabel } from 'utils/formattingUtils';
import { RuleEditor } from 'schemas/IterationRule.schema';
import { RuleActions } from 'reducers/rule/ruleReducer';
import {
  getRuleEditorControl,
  hasVaccineTargetControl,
  updateRuleComparator,
  updateRuleName,
} from 'utils/ruleEditorUtils';
import { ErrorsDisplay } from '../ErrorsDisplay';
import { VaccineTarget } from '../Controls/VaccineTarget';

export interface RadiosEditorProps {
  ruleEditor?: RuleEditor,
  dispatch: Dispatch<RuleActions>;
  value: string;
  conditionType: string;
  errors: Record<string, string>;
}

export function RadiosEditor({
  ruleEditor,
  dispatch,
  value,
  conditionType,
  errors,
}: RadiosEditorProps) {
  const {
    values, defaultValue,
  } = getRuleEditorControl(ruleEditor, 'RADIOS');

  const hasVaccineTarget = hasVaccineTargetControl(ruleEditor);

  const currentValue = value || defaultValue;

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedValue = e.target.value;
    const selectedOption = values.find((option) => option.Value === selectedValue);
    const selectedLabel = selectedOption?.Label ?? selectedValue;
    const formattedVars = conditionType && hasVaccineTarget
      ? [conditionTargetToLabel(conditionType), selectedLabel]
      : [selectedLabel];

    updateRuleComparator(dispatch, ruleEditor, selectedValue, [selectedValue], formattedVars);
  };

  const handleConditionTargetChange = (e: ChangeEvent<HTMLSelectElement>) => {
    dispatch({ type: 'SET_CONDITION_TYPE', payload: e.target.value });

    const selectedOption = values.find((option) => option.Value === currentValue);
    const selectedLabel = selectedOption?.Label ?? currentValue;
    updateRuleName(dispatch, ruleEditor, [conditionTargetToLabel(e.target.value), selectedLabel]);
  };

  return (
    <>
      {hasVaccineTarget && (
        <Row>
          <Col width="full">
            <VaccineTarget
              value={conditionType}
              onChange={handleConditionTargetChange}
            />
          </Col>
        </Row>
      )}
      <Row>
        <Col width="full">
          <Radios
            inline
            name="Radios"
            id="rule-comparator"
            data-testid="rule-comparator"
          >
            {values.map((option) => (
              <Radios.Item
                key={option.Value}
                value={option.Value}
                checked={currentValue === option.Value}
                onChange={handleChange}
                id={`rule-comparator-${option.Value}`}
                data-testid={`rule-comparator-${option.Value}`}
              >
                {option.Label}
              </Radios.Item>
            ))}
          </Radios>
        </Col>
      </Row>
      <ErrorsDisplay errors={errors} errorKeys={['Comparator', 'AttributeName']} />
    </>
  );
}
